import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  ContactPhone as ContactIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  BusinessCenter as BusinessIcon,
  Code as SkillsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

/** Selectable academic levels — only selected ones appear in PDF/DOCX table. */
const ACADEMIC_LEVEL_OPTIONS = [
  { key: 'ssc', label: 'S.S.C / 10th', core: true },
  { key: 'inter', label: 'Intermediate / 12th', core: true },
  { key: 'degree', label: 'Degree', core: true },
  { key: 'btech', label: 'B.Tech.', core: false },
  { key: 'pg', label: 'PG', core: true },
  { key: 'ded', label: 'D.Ed.', core: false },
  { key: 'bed', label: 'B.Ed.', core: false },
  { key: 'iti', label: 'ITI', core: false },
  { key: 'diploma', label: 'Diploma', core: false },
  { key: 'mba', label: 'MBA', core: false },
  { key: 'other', label: 'Other', core: false },
];

const emptyExtraEdu = (key) => ({
  key,
  label: ACADEMIC_LEVEL_OPTIONS.find((o) => o.key === key)?.label || key,
  group: '',
  institution: '',
  board: '',
  year: '',
  percentage: '',
});

const inferSelectedAcademicLevels = (data = {}) => {
  if (Array.isArray(data.selectedAcademicLevels) && data.selectedAcademicLevels.length) {
    return data.selectedAcademicLevels;
  }
  const pj = data.profileJson || data.profile_json || {};
  if (Array.isArray(pj.selectedAcademicLevels) && pj.selectedAcademicLevels.length) {
    return pj.selectedAcademicLevels;
  }
  const out = [];
  if (data.sscSchool || data.sscPercentage || data.sscYear) out.push('ssc');
  if (data.interSchool || data.interPercentage || data.interYear) out.push('inter');
  if (data.degreeName || data.degreeCollege || data.degreePercentage || data.degreeYear) out.push('degree');
  if (data.pgName || data.pgCollege || data.pgPercentage || data.pgYear) out.push('pg');
  const pq = String(data.professionalQualification || '').toLowerCase();
  if (pq.includes('b.ed')) out.push('bed');
  else if (pq.includes('d.ed')) out.push('ded');
  else if (pq.includes('iti')) out.push('iti');
  else if (pq.trim()) out.push('other');
  return out.length ? out : ['ssc', 'inter', 'degree', 'pg'];
};

// Helper function to get the correct token based on route
const getTokenForRoute = () => {
  const path = window.location.pathname;
  // User routes use user_token
  if (path.startsWith('/user/') || path === '/user-login') {
    return localStorage.getItem('user_token');
  }
  // Admin routes use admin_token
  return localStorage.getItem('admin_token');
};

const normalizeTenDigitPhone = (raw) => {
  if (raw == null) return '';
  const digits = String(raw).replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

// Default career objectives (defined outside component to avoid redeclaration)
const careerObjectives = [
  {
    id: 'default1',
    text: 'My objective is to work in a professional atmosphere which would utilize my knowledge and skills and provide myself the scope for widening the spectrum of my knowledge and to secure the position that gives professional satisfaction.'
  },
  {
    id: 'default2',
    text: 'To secure a challenging position in a reputable organization to expand my learnings, knowledge, and skills. To secure a responsible career opportunity to fully utilize my training and skills, while making a significant contribution to the success of the company.'
  },
  {
    id: 'default3',
    text: 'Seeking a position in a dynamic organization where I can launch my career and work towards building a strong skill set. A hard working individual seeking an opportunity to work in a challenging position to prove my skills and utilize my knowledge in the growth of the organization.'
  }
];

const CareerProfileForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [phoneEditable, setPhoneEditable] = useState(false);

  const loginPhone = normalizeTenDigitPhone(
    user?.phone || user?.phone_number || user?.mobile,
  );

  // Form fields
  const [fullName, setFullName] = useState('');
  const [fatherName, setFatherName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('male');
  const [maritalStatus, setMaritalStatus] = useState('single');
  const [nationality, setNationality] = useState('Indian');
  const [languagesKnown, setLanguagesKnown] = useState([]);
  // % or GPA toggles for each qualification
  const [sscIsGPA, setSscIsGPA] = useState(false);
  const [interIsGPA, setInterIsGPA] = useState(false);
  const [degreeIsGPA, setDegreeIsGPA] = useState(false);
  const [pgIsGPA, setPgIsGPA] = useState(false);
  const [itiIsGPA, setItiIsGPA] = useState(false);
  const [bedIsGPA, setBedIsGPA] = useState(false);
  const [dedIsGPA, setDedIsGPA] = useState(false);
  
  // Additional Qualifications
  const [itiName, setItiName] = useState('');
  const [itiPercentage, setItiPercentage] = useState('');
  const [itiInstitution, setItiInstitution] = useState('');
  const [itiYear, setItiYear] = useState('');
  const [bedName, setBedName] = useState('');
  const [bedPercentage, setBedPercentage] = useState('');
  const [bedInstitution, setBedInstitution] = useState('');
  const [bedYear, setBedYear] = useState('');
  const [dedName, setDedName] = useState('');
  const [dedPercentage, setDedPercentage] = useState('');
  const [dedInstitution, setDedInstitution] = useState('');
  const [dedYear, setDedYear] = useState('');
  const [currentPosition, setCurrentPosition] = useState('');
  const [experienceType, setExperienceType] = useState('');
  const [experienceYears, setExperienceYears] = useState(0);
  const [expectedSalary, setExpectedSalary] = useState('');
  const [skills, setSkills] = useState('');
  const [languages, setLanguages] = useState('');
  const [objective, setObjective] = useState(careerObjectives[0].text);
  const [objectiveOption, setObjectiveOption] = useState('default1'); // Radio button selection
  const [otherLanguage, setOtherLanguage] = useState(''); // For "Other" language option
  const [showOtherLanguage, setShowOtherLanguage] = useState(false); // Show/hide other language text field
  const [selectedLanguages, setSelectedLanguages] = useState([]); // Checkbox selections
  const [experiences, setExperiences] = useState([{ designation: '', company: '', fromDate: '', toDate: '', location: '' }]); // Multiple experiences

  // Options
  const genderOptions = ['male', 'female', 'other'];
  const maritalStatusOptions = ['single', 'married', 'divorced', 'widowed'];

  // Education fields
  const [sscPercentage, setSscPercentage] = useState('');
  const [sscSchool, setSscSchool] = useState('');
  const [sscYear, setSscYear] = useState('');
  const [interPercentage, setInterPercentage] = useState('');
  const [interSchool, setInterSchool] = useState('');
  const [interYear, setInterYear] = useState('');
  const [degreeName, setDegreeName] = useState('');
  const [degreePercentage, setDegreePercentage] = useState('');
  const [degreeCollege, setDegreeCollege] = useState('');
  const [degreeYear, setDegreeYear] = useState('');
  const [pgName, setPgName] = useState('');
  const [pgPercentage, setPgPercentage] = useState('');
  const [pgCollege, setPgCollege] = useState('');
  const [pgYear, setPgYear] = useState('');
  const [selectedAcademicLevels, setSelectedAcademicLevels] = useState([
    'ssc',
    'inter',
    'degree',
    'pg',
  ]);
  const [extraEducations, setExtraEducations] = useState([]);
  const [otherQualificationLabel, setOtherQualificationLabel] = useState('');

  // Options (genderOptions, maritalStatusOptions are defined above)
  const languageOptions = ['Telugu', 'English', 'Hindi', 'Tamil', 'Kannada', 'Malayalam', 'Marathi', 'Gujarati'];

  const toggleAcademicLevel = (key) => {
    setSelectedAcademicLevels((prev) => {
      if (prev.includes(key)) {
        setExtraEducations((ex) => ex.filter((e) => e.key !== key));
        return prev.filter((k) => k !== key);
      }
      if (!ACADEMIC_LEVEL_OPTIONS.find((o) => o.key === key)?.core) {
        setExtraEducations((ex) =>
          ex.some((e) => e.key === key) ? ex : [...ex, emptyExtraEdu(key)],
        );
      }
      return [...prev, key];
    });
  };

  const updateExtraEdu = (key, field, value) => {
    setExtraEducations((prev) =>
      prev.map((e) => (e.key === key ? { ...e, [field]: value } : e)),
    );
  };

  const hasLevel = (key) => selectedAcademicLevels.includes(key);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/career-profiles/settings`);
        if (res.data?.success && res.data.settings) {
          setPhoneEditable(Boolean(res.data.settings.phone_editable));
        }
      } catch (err) {
        console.warn('Could not load career phone settings, defaulting to locked');
        setPhoneEditable(false);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (user?.id || user?.userId) {
      loadProfile();
    } else if (loginPhone) {
      setPhone(loginPhone);
    }
  }, [user]);

  useEffect(() => {
    if (!phoneEditable && loginPhone) {
      setPhone(loginPhone);
    }
  }, [phoneEditable, loginPhone]);

  const loadProfile = async () => {
    // Try multiple ways to get user ID
    let userId = user?.id || user?.userId || user?.user_id;
    
    // Fallback: use phone number if ID is not available
    if (!userId && (user?.phone || user?.phone_number)) {
      const phone = user.phone || user.phone_number;
      userId = phone.replace(/\D/g, '').slice(-10);
    }
    
    if (!userId) {
      console.warn('No user ID found. User object:', user);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const token = getTokenForRoute();
      const response = await axios.get(`${API_BASE_URL}/career-profiles/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success && response.data.data) {
        const data = response.data.data.profile_json || response.data.data;
        setProfile(data);
        populateForm(data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('No existing profile found, starting fresh');
        if (loginPhone) setPhone(loginPhone);
      } else {
        console.error('Error loading profile:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (data) => {
    setFullName(data.fullName || '');
    setFatherName(data.fatherName || '');
    setEmail(data.email || user?.email || '');
    // Prefer login profile phone; lock unless AA enables editing
    const profilePhone = normalizeTenDigitPhone(data.phone);
    setPhone(loginPhone || profilePhone || '');
    setAddress(data.address || '');
    setCity(data.city || '');
    setState(data.state || '');
    setPincode(data.pincode || '');
    setDateOfBirth(data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '');
    setGender(data.gender || 'male');
    setMaritalStatus(data.maritalStatus || 'single');
    setNationality(data.nationality || 'Indian');
    setLanguagesKnown(data.languagesKnown || []);
    
    // Check if percentage values contain GPA indicators
    const sscPerc = data.sscPercentage || '';
    const interPerc = data.interPercentage || '';
    const degreePerc = data.degreePercentage || '';
    const pgPerc = data.pgPercentage || '';
    setSscIsGPA(sscPerc.toLowerCase().includes('gpa'));
    setInterIsGPA(interPerc.toLowerCase().includes('gpa'));
    setDegreeIsGPA(degreePerc.toLowerCase().includes('gpa'));
    setPgIsGPA(pgPerc.toLowerCase().includes('gpa'));
    
    // Extract percentage/GPA values (remove % or GPA suffix)
    setSscPercentage(sscPerc.replace(/\s*(%|GPA)/gi, ''));
    setInterPercentage(interPerc.replace(/\s*(%|GPA)/gi, ''));
    setDegreePercentage(degreePerc.replace(/\s*(%|GPA)/gi, ''));
    setPgPercentage(pgPerc.replace(/\s*(%|GPA)/gi, ''));
    setCurrentPosition(data.currentPosition || '');
    setExperienceType(data.experienceType || '');
    setExperienceYears(data.experienceYears || 0);
    setExpectedSalary(data.expectedSalary || '');
    setSkills(data.skills?.join(', ') || '');
    
    // Handle languages - convert from comma-separated to checkbox array
    const langArray = data.languages?.map(l => l.trim()) || [];
    setSelectedLanguages(langArray.filter(l => ['Telugu', 'English', 'Hindi'].includes(l)));
    const otherLangs = langArray.filter(l => !['Telugu', 'English', 'Hindi'].includes(l));
    setOtherLanguage(otherLangs.join(', '));
    setShowOtherLanguage(otherLangs.length > 0);
    
    // Handle objective - check if it matches any default option
    const currentObjective = data.objective || '';
    const matchedObjective = careerObjectives.find(obj => obj.text === currentObjective);
    if (matchedObjective) {
      setObjectiveOption(matchedObjective.id);
      setObjective(currentObjective);
    } else if (currentObjective) {
      setObjectiveOption('custom');
      setObjective(currentObjective);
    } else {
      setObjectiveOption('default1');
      setObjective(careerObjectives[0].text);
    }
    
    // Set default objective if not set
    if (!objective) {
      setObjective(careerObjectives[0].text);
      setObjectiveOption('default1');
    }
    
    // Handle experiences - convert single experience to array format
    if (data.designation || data.company || data.fromDate || data.toDate) {
      setExperiences([{
        designation: data.designation || '',
        company: data.company || '',
        fromDate: data.fromDate || '',
        toDate: data.toDate || '',
        location: data.location || ''
      }]);
    } else {
      setExperiences([{ designation: '', company: '', fromDate: '', toDate: '', location: '' }]);
    }
    setSscSchool(data.sscSchool || '');
    setSscYear(data.sscYear || '');
    setInterSchool(data.interSchool || '');
    setInterYear(data.interYear || '');
    setDegreeName(data.degreeName || '');
    setDegreeCollege(data.degreeCollege || '');
    setDegreeYear(data.degreeYear || '');
    setPgName(data.pgName || '');
    setPgCollege(data.pgCollege || '');
    setPgYear(data.pgYear || '');

    const levels = inferSelectedAcademicLevels(data);
    setSelectedAcademicLevels(levels);
    const pj = data.profileJson || data.profile_json || {};
    const extras = Array.isArray(pj.extraEducations) ? pj.extraEducations : [];
    if (extras.length) {
      setExtraEducations(extras);
      const other = extras.find((e) => e.key === 'other');
      if (other?.label) setOtherQualificationLabel(other.label);
    } else {
      // Seed extra rows for non-core selected levels from legacy professional fields
      const seeded = levels
        .filter((k) => !['ssc', 'inter', 'degree', 'pg'].includes(k))
        .map((k) => emptyExtraEdu(k));
      setExtraEducations(seeded);
    }
    
    // Additional qualifications — prefer dedicated professional_* then extras
    const profQual = data.professionalQualification || '';
    if (profQual.includes('ITI:')) {
      setItiName(profQual.replace('ITI:', '').trim());
      setItiPercentage((data.professionalPercentage || '').replace(/\s*(%|GPA)/gi, ''));
      setItiInstitution(data.professionalInstitution || '');
      setItiYear(data.professionalYear || '');
      setItiIsGPA((data.professionalPercentage || '').toLowerCase().includes('gpa'));
    } else if (profQual.includes('B.Ed:')) {
      setBedName(profQual.replace('B.Ed:', '').trim());
      setBedPercentage((data.professionalPercentage || '').replace(/\s*(%|GPA)/gi, ''));
      setBedInstitution(data.professionalInstitution || '');
      setBedYear(data.professionalYear || '');
      setBedIsGPA((data.professionalPercentage || '').toLowerCase().includes('gpa'));
    } else if (profQual.includes('D.Ed:')) {
      setDedName(profQual.replace('D.Ed:', '').trim());
      setDedPercentage((data.professionalPercentage || '').replace(/\s*(%|GPA)/gi, ''));
      setDedInstitution(data.professionalInstitution || '');
      setDedYear(data.professionalYear || '');
      setDedIsGPA((data.professionalPercentage || '').toLowerCase().includes('gpa'));
    } else {
      const hydrateDedicated = (key, setName, setPct, setInst, setYear, setGpa) => {
        const ex = (extras.length ? extras : []).find((e) => e.key === key);
        if (!ex) return;
        setName(ex.group || '');
        const pct = String(ex.percentage || '');
        setPct(pct.replace(/\s*(%|GPA)/gi, ''));
        setInst(ex.institution || '');
        setYear(ex.year || '');
        setGpa(pct.toLowerCase().includes('gpa'));
      };
      hydrateDedicated('iti', setItiName, setItiPercentage, setItiInstitution, setItiYear, setItiIsGPA);
      hydrateDedicated('bed', setBedName, setBedPercentage, setBedInstitution, setBedYear, setBedIsGPA);
      hydrateDedicated('ded', setDedName, setDedPercentage, setDedInstitution, setDedYear, setDedIsGPA);
    }
    // Experiences are now handled in the previous block
  };

  const calculateCompletion = () => {
    let completed = 0;
    let total = 7;
    
    if (fullName) completed++;
    if (email) completed++;
    if (phone) completed++;
    if (dateOfBirth) completed++;
    if (skills) completed++;
    if (sscPercentage) completed++;
    if (address) completed++;
    
    return Math.round((completed / total) * 100);
  };
  
  const formatPercentage = (value, isGPA) => {
    if (!value) return '';
    return isGPA ? `${value} GPA` : `${value}%`;
  };

  /** Merge dedicated ITI/B.Ed/D.Ed fields into extraEducations for resume rows. */
  const buildExtraEducationsPayload = () => {
    const byKey = {};
    for (const e of extraEducations) {
      if (e?.key) byKey[e.key] = { ...e };
    }
    const upsert = (key, label, group, percentage, isGPA, institution, year) => {
      if (!selectedAcademicLevels.includes(key)) {
        delete byKey[key];
        return;
      }
      byKey[key] = {
        key,
        label,
        group: group || '',
        institution: institution || '',
        board: byKey[key]?.board || '',
        year: year || '',
        percentage: percentage ? formatPercentage(percentage, isGPA) : '',
      };
    };
    upsert('iti', 'ITI', itiName, itiPercentage, itiIsGPA, itiInstitution, itiYear);
    upsert('bed', 'B.Ed.', bedName, bedPercentage, bedIsGPA, bedInstitution, bedYear);
    upsert('ded', 'D.Ed.', dedName, dedPercentage, dedIsGPA, dedInstitution, dedYear);
    if (selectedAcademicLevels.includes('other') && otherQualificationLabel.trim()) {
      byKey.other = {
        ...(byKey.other || emptyExtraEdu('other')),
        key: 'other',
        label: otherQualificationLabel.trim(),
      };
    }
    // Keep only extras for currently selected non-core levels
    return selectedAcademicLevels
      .filter((k) => !['ssc', 'inter', 'degree', 'pg'].includes(k))
      .map((k) => byKey[k] || emptyExtraEdu(k));
  };

  const handleSave = async () => {
    // Try multiple ways to get user ID
    let userId = user?.id || user?.userId || user?.user_id;
    
    // Fallback: use phone number if ID is not available (for users identified by phone)
    if (!userId && (user?.phone || user?.phone_number)) {
      const phone = user.phone || user.phone_number;
      // Remove +91 prefix and use last 10 digits
      userId = phone.replace(/\D/g, '').slice(-10);
      console.warn('Using phone number as user ID:', userId);
    }
    
    if (!userId) {
      console.error('User object:', user);
      toast.error('User not authenticated. Please login again.');
      return;
    }

    if (!fullName || !email || !phone) {
      toast.error('Please fill in required fields: Name, Email, Phone');
      return;
    }

    setSaving(true);
    try {
      const token = getTokenForRoute();
      if (!token) {
        toast.error('Authentication token missing. Please login again.');
        setSaving(false);
        return;
      }

      const profileData = {
        userId: userId,
        fullName,
        fatherName,
        email,
        phone,
        address,
        city,
        state,
        pincode,
        dateOfBirth: dateOfBirth ? new Date(dateOfBirth).toISOString() : new Date().toISOString(),
        gender,
        maritalStatus,
        nationality,
        languagesKnown,
        qualification: 'tenth', // Default, not used anymore
        interestedCategories: [],
        preferredFilters: [],
        preferredLocation: '',
        isWillingToRelocate: false,
        currentPosition,
        experienceType,
        experienceYears: parseInt(experienceYears) || 0,
        expectedSalary,
        skills: skills.split(',').map(s => s.trim()).filter(s => s),
        languages: [
          ...selectedLanguages,
          ...(otherLanguage ? otherLanguage.split(',').map(l => l.trim()).filter(l => l) : [])
        ],
        // Use first experience for backward compatibility
        designation: experiences[0]?.designation || '',
        fromDate: experiences[0]?.fromDate || '',
        toDate: experiences[0]?.toDate || '',
        company: experiences[0]?.company || '',
        location: experiences[0]?.location || '',
        experiences: experiences, // Store all experiences
        objective: objectiveOption === 'custom' ? objective : (careerObjectives.find(obj => obj.id === objectiveOption)?.text || objective),
        sscPercentage: formatPercentage(sscPercentage, sscIsGPA),
        sscSchool,
        sscYear,
        interPercentage: formatPercentage(interPercentage, interIsGPA),
        interSchool,
        interYear,
        degreeName,
        degreePercentage: formatPercentage(degreePercentage, degreeIsGPA),
        degreeCollege,
        degreeYear,
        pgName,
        pgPercentage: formatPercentage(pgPercentage, pgIsGPA),
        pgCollege,
        pgYear,
        professionalQualification: itiName 
          ? `ITI: ${itiName}` 
          : (bedName 
              ? `B.Ed: ${bedName}` 
              : (dedName 
                  ? `D.Ed: ${dedName}` 
                  : '')),
        professionalPercentage: itiPercentage
          ? formatPercentage(itiPercentage, itiIsGPA)
          : (bedPercentage
              ? formatPercentage(bedPercentage, bedIsGPA)
              : (dedPercentage
                  ? formatPercentage(dedPercentage, dedIsGPA)
                  : '')),
        professionalInstitution: itiInstitution || bedInstitution || dedInstitution || '',
        professionalYear: itiYear || bedYear || dedYear || '',
        selectedAcademicLevels,
        extraEducations: buildExtraEducationsPayload(),
        profileJson: {
          userId: userId,
          fullName,
          fatherName,
          email,
          phone,
          address,
          city,
          state,
          pincode,
          dateOfBirth: dateOfBirth ? new Date(dateOfBirth).toISOString() : new Date().toISOString(),
          gender,
          maritalStatus,
          nationality,
          languagesKnown,
          qualification: 'tenth',
          interestedCategories: [],
          preferredFilters: [],
          preferredLocation: '',
          isWillingToRelocate: false,
          currentPosition,
          experienceType,
          experienceYears: parseInt(experienceYears) || 0,
          expectedSalary,
          skills: skills.split(',').map(s => s.trim()).filter(s => s),
          languages: [
            ...selectedLanguages,
            ...(otherLanguage ? otherLanguage.split(',').map(l => l.trim()).filter(l => l) : [])
          ],
          // Use first experience for backward compatibility
          designation: experiences[0]?.designation || '',
          fromDate: experiences[0]?.fromDate || '',
          toDate: experiences[0]?.toDate || '',
          company: experiences[0]?.company || '',
          location: experiences[0]?.location || '',
          experiences: experiences, // Store all experiences
          objective: objectiveOption === 'custom' ? objective : (careerObjectives.find(obj => obj.id === objectiveOption)?.text || objective),
          sscPercentage: formatPercentage(sscPercentage, sscIsGPA),
          sscSchool,
          sscYear,
          interPercentage: formatPercentage(interPercentage, interIsGPA),
          interSchool,
          interYear,
          degreeName,
          degreePercentage: formatPercentage(degreePercentage, degreeIsGPA),
          degreeCollege,
          degreeYear,
          pgName,
          pgPercentage: formatPercentage(pgPercentage, pgIsGPA),
          pgCollege,
          pgYear,
          selectedAcademicLevels,
          extraEducations: buildExtraEducationsPayload(),
          professionalQualification: itiName 
            ? `ITI: ${itiName}` 
            : (bedName 
                ? `B.Ed: ${bedName}` 
                : (dedName 
                    ? `D.Ed: ${dedName}` 
                    : '')),
          professionalPercentage: itiPercentage
            ? formatPercentage(itiPercentage, itiIsGPA)
            : (bedPercentage
                ? formatPercentage(bedPercentage, bedIsGPA)
                : (dedPercentage
                    ? formatPercentage(dedPercentage, dedIsGPA)
                    : '')),
          professionalInstitution: itiInstitution || bedInstitution || dedInstitution || '',
          professionalYear: itiYear || bedYear || dedYear || '',
          lastUpdated: new Date().toISOString(),
        },
        lastUpdated: new Date().toISOString(),
      };

      const response = await axios.post(`${API_BASE_URL}/career-profiles`, profileData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.data.success) {
        toast.success('Profile saved successfully!');
        setProfile(profileData.profileJson);
      } else {
        throw new Error(response.data.message || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Save error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Request data:', { userId, fullName, email, phone });
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. You can only save your own profile.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid data. Please check all fields.');
      } else {
        toast.error(error.response?.data?.message || `Failed to save profile: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Typography>Loading profile...</Typography>
        </Box>
      </Container>
    );
  }

  const completionPercentage = calculateCompletion();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/user/dashboard')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Career Zone Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete your profile to get matched with relevant job opportunities
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' } }}
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </Box>

      {/* Progress Bar */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" fontWeight={600}>
              Profile Completion
            </Typography>
            <Typography variant="body1" fontWeight={600} color="primary">
              {completionPercentage}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={completionPercentage} sx={{ height: 8, borderRadius: 4 }} />
        </CardContent>
      </Card>

      {/* Form Sections */}
      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: '#667eea' }} />
                <Typography variant="h6" fontWeight={700}>
                  Personal Information (వ్యక్తిగత సమాచారం)
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name *"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Father's Name"
                    value={fatherName}
                    onChange={(e) => setFatherName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone *"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    disabled={!phoneEditable}
                    helperText={
                      phoneEditable
                        ? undefined
                        : 'From login profile (locked — AA can enable editing)'
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date of Birth *"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select value={gender} onChange={(e) => setGender(e.target.value)} label="Gender">
                      {genderOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Marital Status</InputLabel>
                    <Select value={maritalStatus} onChange={(e) => setMaritalStatus(e.target.value)} label="Marital Status">
                      {maritalStatusOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nationality"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Education Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, color: '#667eea' }} />
                <Typography variant="h6" fontWeight={700}>
                  Education Details (చదువు వివరాలు)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Select the qualifications to include. Only selected ones appear as rows in PDF / Word.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {ACADEMIC_LEVEL_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.key}
                    label={opt.label}
                    color={hasLevel(opt.key) ? 'primary' : 'default'}
                    variant={hasLevel(opt.key) ? 'filled' : 'outlined'}
                    onClick={() => toggleAcademicLevel(opt.key)}
                    clickable
                  />
                ))}
              </Box>
              {hasLevel('other') && (
                <TextField
                  fullWidth
                  size="small"
                  label="Other qualification name"
                  placeholder="e.g. Polytechnic, CA Foundation…"
                  value={otherQualificationLabel}
                  onChange={(e) => setOtherQualificationLabel(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}
              <Grid container spacing={2}>
                {hasLevel('ssc') && (
                <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>SSC / 10th</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={sscIsGPA ? "GPA" : "Percentage"}
                    value={sscPercentage}
                    onChange={(e) => setSscPercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: sscIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sscIsGPA}
                        onChange={(e) => setSscIsGPA(e.target.checked)}
                      />
                    }
                    label={sscIsGPA ? "GPA Mode" : "Percentage Mode"}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="School Name"
                    value={sscSchool}
                    onChange={(e) => setSscSchool(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={sscYear}
                    onChange={(e) => setSscYear(e.target.value)}
                  />
                </Grid>
                </>
                )}
                {hasLevel('inter') && (
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Intermediate / 12th</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={interIsGPA ? "GPA" : "Percentage"}
                    value={interPercentage}
                    onChange={(e) => setInterPercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: interIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={interIsGPA}
                        onChange={(e) => setInterIsGPA(e.target.checked)}
                      />
                    }
                    label={interIsGPA ? "GPA Mode" : "Percentage Mode"}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="School Name"
                    value={interSchool}
                    onChange={(e) => setInterSchool(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={interYear}
                    onChange={(e) => setInterYear(e.target.value)}
                  />
                </Grid>
                </>
                )}
                {hasLevel('degree') && (
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Degree</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Degree Name"
                    value={degreeName}
                    onChange={(e) => setDegreeName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={degreeIsGPA ? "GPA" : "Percentage"}
                    value={degreePercentage}
                    onChange={(e) => setDegreePercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: degreeIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={degreeIsGPA}
                        onChange={(e) => setDegreeIsGPA(e.target.checked)}
                      />
                    }
                    label={degreeIsGPA ? "GPA" : "%"}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="College Name"
                    value={degreeCollege}
                    onChange={(e) => setDegreeCollege(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={degreeYear}
                    onChange={(e) => setDegreeYear(e.target.value)}
                  />
                </Grid>
                </>
                )}
                {hasLevel('pg') && (
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Post Graduation (PG)</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="PG Name"
                    value={pgName}
                    onChange={(e) => setPgName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={pgIsGPA ? "GPA" : "Percentage"}
                    value={pgPercentage}
                    onChange={(e) => setPgPercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: pgIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pgIsGPA}
                        onChange={(e) => setPgIsGPA(e.target.checked)}
                      />
                    }
                    label={pgIsGPA ? "GPA" : "%"}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="College Name"
                    value={pgCollege}
                    onChange={(e) => setPgCollege(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={pgYear}
                    onChange={(e) => setPgYear(e.target.value)}
                  />
                </Grid>
                </>
                )}

                {extraEducations
                  .filter((e) =>
                    hasLevel(e.key) &&
                    !['ssc', 'inter', 'degree', 'pg', 'iti', 'bed', 'ded'].includes(e.key),
                  )
                  .map((edu) => (
                    <React.Fragment key={edu.key}>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                          {edu.key === 'other' && otherQualificationLabel.trim()
                            ? otherQualificationLabel.trim()
                            : edu.label || edu.key.toUpperCase()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Group / Specialization"
                          value={edu.group || ''}
                          onChange={(e) => updateExtraEdu(edu.key, 'group', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Institution"
                          value={edu.institution || ''}
                          onChange={(e) => updateExtraEdu(edu.key, 'institution', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Board / University"
                          value={edu.board || ''}
                          onChange={(e) => updateExtraEdu(edu.key, 'board', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Year"
                          value={edu.year || ''}
                          onChange={(e) => updateExtraEdu(edu.key, 'year', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Percentage / GPA"
                          value={edu.percentage || ''}
                          onChange={(e) => updateExtraEdu(edu.key, 'percentage', e.target.value)}
                        />
                      </Grid>
                    </React.Fragment>
                  ))}

                {hasLevel('iti') && (
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>ITI (Industrial Training Institute)</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="ITI Trade"
                    value={itiName}
                    onChange={(e) => setItiName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={itiIsGPA ? "GPA" : "Percentage"}
                    value={itiPercentage}
                    onChange={(e) => setItiPercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: itiIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={itiIsGPA}
                        onChange={(e) => setItiIsGPA(e.target.checked)}
                      />
                    }
                    label={itiIsGPA ? "GPA" : "%"}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Institution Name"
                    value={itiInstitution}
                    onChange={(e) => setItiInstitution(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={itiYear}
                    onChange={(e) => setItiYear(e.target.value)}
                  />
                </Grid>
                </>
                )}
                {hasLevel('bed') && (
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>B.Ed (Bachelor of Education)</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="B.Ed Course"
                    value={bedName}
                    onChange={(e) => setBedName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={bedIsGPA ? "GPA" : "Percentage"}
                    value={bedPercentage}
                    onChange={(e) => setBedPercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: bedIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bedIsGPA}
                        onChange={(e) => setBedIsGPA(e.target.checked)}
                      />
                    }
                    label={bedIsGPA ? "GPA" : "%"}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Institution Name"
                    value={bedInstitution}
                    onChange={(e) => setBedInstitution(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={bedYear}
                    onChange={(e) => setBedYear(e.target.value)}
                  />
                </Grid>
                </>
                )}
                {hasLevel('ded') && (
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>D.Ed (Diploma in Education)</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="D.Ed Course"
                    value={dedName}
                    onChange={(e) => setDedName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={dedIsGPA ? "GPA" : "Percentage"}
                    value={dedPercentage}
                    onChange={(e) => setDedPercentage(e.target.value)}
                    type="number"
                    inputProps={{ step: dedIsGPA ? "0.1" : "1" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dedIsGPA}
                        onChange={(e) => setDedIsGPA(e.target.checked)}
                      />
                    }
                    label={dedIsGPA ? "GPA" : "%"}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Institution Name"
                    value={dedInstitution}
                    onChange={(e) => setDedInstitution(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Year"
                    value={dedYear}
                    onChange={(e) => setDedYear(e.target.value)}
                  />
                </Grid>
                </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Professional Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WorkIcon sx={{ mr: 1, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={700}>
                    Experience (వృత్తి అనుభవం)
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Position"
                    value={currentPosition}
                    onChange={(e) => setCurrentPosition(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Experience Type</InputLabel>
                    <Select value={experienceType} onChange={(e) => setExperienceType(e.target.value)} label="Experience Type">
                      <MenuItem value="fresher">Fresher</MenuItem>
                      <MenuItem value="experienced">Experienced</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Years of Experience"
                    type="number"
                    value={experienceYears}
                    onChange={(e) => setExperienceYears(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Expected Salary"
                    value={expectedSalary}
                    onChange={(e) => setExpectedSalary(e.target.value)}
                  />
                </Grid>
                
                {/* Multiple Experience Entries */}
                {experiences.map((exp, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Experience {index + 1}
                        </Typography>
                        {experiences.length > 1 && (
                          <IconButton
                            color="error"
                            onClick={() => {
                              const newExps = experiences.filter((_, i) => i !== index);
                              setExperiences(newExps);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Designation"
                        value={exp.designation}
                        onChange={(e) => {
                          const newExps = [...experiences];
                          newExps[index].designation = e.target.value;
                          setExperiences(newExps);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company"
                        value={exp.company}
                        onChange={(e) => {
                          const newExps = [...experiences];
                          newExps[index].company = e.target.value;
                          setExperiences(newExps);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="From Date"
                        type="date"
                        value={exp.fromDate}
                        onChange={(e) => {
                          const newExps = [...experiences];
                          newExps[index].fromDate = e.target.value;
                          setExperiences(newExps);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="To Date"
                        type="date"
                        value={exp.toDate}
                        onChange={(e) => {
                          const newExps = [...experiences];
                          newExps[index].toDate = e.target.value;
                          setExperiences(newExps);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Location"
                        value={exp.location}
                        onChange={(e) => {
                          const newExps = [...experiences];
                          newExps[index].location = e.target.value;
                          setExperiences(newExps);
                        }}
                      />
                    </Grid>
                    {index < experiences.length - 1 && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setExperiences([...experiences, { designation: '', company: '', fromDate: '', toDate: '', location: '' }]);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Add Another Experience
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                      Career Objective
                    </FormLabel>
                    <RadioGroup
                      value={objectiveOption}
                      onChange={(e) => {
                        setObjectiveOption(e.target.value);
                        if (e.target.value !== 'custom') {
                          const selectedObj = careerObjectives.find(obj => obj.id === e.target.value);
                          if (selectedObj) {
                            setObjective(selectedObj.text);
                          }
                        }
                      }}
                    >
                      {careerObjectives.map((obj) => (
                        <FormControlLabel
                          key={obj.id}
                          value={obj.id}
                          control={<Radio />}
                          label={
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {obj.text}
                            </Typography>
                          }
                          sx={{ mb: 1, alignItems: 'flex-start' }}
                        />
                      ))}
                      <FormControlLabel
                        value="custom"
                        control={<Radio />}
                        label="Custom Objective"
                        sx={{ mb: 1 }}
                      />
                    </RadioGroup>
                    {objectiveOption === 'custom' && (
                      <TextField
                        fullWidth
                        label="Custom Career Objective"
                        multiline
                        rows={3}
                        value={objective}
                        onChange={(e) => setObjective(e.target.value)}
                        placeholder="Write your custom career objective..."
                        sx={{ mt: 2 }}
                      />
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Skills & Languages */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SkillsIcon sx={{ mr: 1, color: '#667eea' }} />
                <Typography variant="h6" fontWeight={700}>
                  Skills & Languages (నైపుణ్యాలు & భాషలు)
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Skills * (comma-separated)"
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                    placeholder="e.g., JavaScript, Python, Communication"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                    Languages Known
                  </FormLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['Telugu', 'English', 'Hindi'].map((lang) => (
                      <FormControlLabel
                        key={lang}
                        control={
                          <Checkbox
                            checked={selectedLanguages.includes(lang)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLanguages([...selectedLanguages, lang]);
                              } else {
                                setSelectedLanguages(selectedLanguages.filter(l => l !== lang));
                              }
                            }}
                          />
                        }
                        label={lang}
                      />
                    ))}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showOtherLanguage}
                          onChange={(e) => {
                            setShowOtherLanguage(e.target.checked);
                            if (!e.target.checked) {
                              setOtherLanguage('');
                            }
                          }}
                        />
                      }
                      label="Other"
                    />
                    {showOtherLanguage && (
                      <TextField
                        fullWidth
                        label="Other Languages (comma-separated)"
                        value={otherLanguage}
                        onChange={(e) => setOtherLanguage(e.target.value)}
                        placeholder="e.g., Tamil, Urdu, Kannada"
                        sx={{ ml: 4, mt: 1 }}
                        size="small"
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/user/dashboard')}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' } }}
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
        <Button
          variant="contained"
          startIcon={<SchoolIcon />}
          onClick={() => navigate('/user/resume')}
          sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
        >
          Resume Generator
        </Button>
      </Box>
    </Container>
  );
};

export default CareerProfileForm;

